import getIn from 'transmute/getIn';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
// @ts-expect-error untyped module
import getNativeCurrencySymbol from 'I18n/utils/getNativeCurrencySymbol';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import { FUTURE_START_DATE, RELATIVE_START_DATE } from '../constants/LineItems';
import { getLineItemRecurringFrequency } from '../utils/recurring';
import { useSubtotals } from '../context/SubtotalsProvider';
import { isRecurringCheckoutLineItem } from '../types/utils/typeguards';
import RecurringSubscriptionSubtext from './RecurringSubscriptionSubtext';
import { isNone } from '../types/utils/helpers';
import { useTransactionLimits } from '../hooks/useTransactionLimits';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RecurringTotalsMessage = ({
  currencyCode
}) => {
  const {
    recurringSubtotals = [],
    lineItems = [],
    adjustmentAmounts = []
  } = useSubtotals();
  const {
    limits
  } = useTransactionLimits();
  const {
    formState
  } = usePaymentFormContext();
  const paymentMethod = formState.get('paymentMethod');
  const minimumAmount = limits && limits.minimumAmountForPaymentMethod[paymentMethod];
  const maximumAmount = limits && limits.maximumAmountForPaymentMethod[paymentMethod];
  if (recurringSubtotals.length === 0) {
    return null;
  }
  const recurringSubtotal = recurringSubtotals[0].subtotal;
  const isRecurringSubtotalBelowMin = minimumAmount && recurringSubtotal < minimumAmount;
  const isRecurringSubtotalAboveMax = maximumAmount && recurringSubtotal > maximumAmount;
  const recurringLineItems = lineItems.filter(isRecurringCheckoutLineItem);
  const recurringBillingFrequency = getLineItemRecurringFrequency(recurringLineItems[0]);
  const numPayments = recurringLineItems[0] && recurringLineItems[0].recurringBillingTerms.numPayments;
  const initialPayment = recurringSubtotals[0].recurringBillingTerms.initialPayment;
  const hasFixedBillingStartDate = initialPayment.type === FUTURE_START_DATE;
  const hasRelativeBillingStartDate = initialPayment.type === RELATIVE_START_DATE;
  const hasMultipleSubscriptions = recurringSubtotals.length > 1;
  const hasCheckoutFees = adjustmentAmounts.some(adjustment => adjustment.adjustmentType === 'CHECKOUT_FEE');

  /**
   * numSubsequentPayments will be the same for future payments since all payments are in the future but
   * for a current charge it will be 1 less since one numPayment happens at checkout
   */
  const numSubsequentPayments = !numPayments || hasFixedBillingStartDate || hasRelativeBillingStartDate ? numPayments : numPayments - 1;
  const shouldRenderOpenAmountPlaceholder = recurringSubtotal === 0 && lineItems.some(lineItem => getIn(['customConfiguration', 'hasEditablePrice'], lineItem));
  if (numSubsequentPayments === 0) {
    return /*#__PURE__*/_jsx("span", {
      "data-test-id": "recurring-totals-message",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.noFuturePayments"
      })
    });
  }
  if (hasFixedBillingStartDate || hasRelativeBillingStartDate) {
    return null;
  }
  if (hasMultipleSubscriptions) {
    return /*#__PURE__*/_jsx("span", {
      "data-test-id": "recurring-totals-message",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.recurringPaymentsForMultipleSubscriptions"
      })
    });
  }
  if (shouldRenderOpenAmountPlaceholder) {
    const openAmountPlaceholder = /*#__PURE__*/_jsx(Text, {
      as: "small",
      typography: "heading-25",
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.openAmount",
        options: {
          currencySymbol: getNativeCurrencySymbol(currencyCode)
        }
      })
    });
    return /*#__PURE__*/_jsx("span", {
      "data-test-id": "recurring-totals-message",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: isNone(numPayments) ? 'payments.indefiniteRecurringPayments_jsx' : 'payments.recurringNumPayments_jsx',
        options: {
          priceProps: {
            message: `payments.recurringBillingTerms.${recurringBillingFrequency}`,
            options: {
              price: openAmountPlaceholder
            },
            simplify: false
          },
          count: numSubsequentPayments
        },
        elements: {
          FormattedPrice: FormattedReactMessage
        }
      })
    });
  }
  return /*#__PURE__*/_jsxs("span", {
    "data-test-id": "recurring-totals-message",
    children: [/*#__PURE__*/_jsx(RecurringSubscriptionSubtext, {
      currencyCode: currencyCode,
      recurringSubtotal: recurringSubtotals[0],
      hasCheckoutFees: hasCheckoutFees
    }), isRecurringSubtotalBelowMin && /*#__PURE__*/_jsx(Text, {
      as: "small",
      "data-test-id": "recurring-payment-totals-below-min",
      color: "text-alert-default",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        elements: {
          Price: FormattedIntlCurrency
        },
        message: "payments.recurringPaymentTotalsBelowMinError_jsx",
        options: {
          value: minimumAmount,
          currencyCode
        }
      })
    }), isRecurringSubtotalAboveMax && /*#__PURE__*/_jsx(Text, {
      as: "small",
      "data-test-id": "recurring-payment-totals-above-max",
      color: "text-alert-default",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        elements: {
          Price: FormattedIntlCurrency
        },
        message: "payments.recurringPaymentTotalsAboveMaxError_jsx",
        options: {
          value: maximumAmount,
          currencyCode
        }
      })
    })]
  });
};
export default RecurringTotalsMessage;